import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { LinkFavicon } from '#pplx/frontend/components/links/LinkFavicon.js';
import React, { useCallback, useEffect, useState } from 'react';
const HOVER_CARD_WIDTH_PX = 280;
/**
 * Wraps an arbitrary trigger element in the file hover-preview popover (preview
 * image + full filename). Trigger-agnostic so it can sit around a plain button
 * (see FileButton) or an existing interactive row (see AssetHoverPreview)
 * without imposing its own button. `onContentClick` fires when the preview card
 * itself is clicked.
 */
export function FileHoverPreview({ name, icon, faviconDomain, typeLabel, preview, side = 'bottom', widthPx = HOVER_CARD_WIDTH_PX, offsetPx, triggerClassName = 'min-w-0', showHoverCard = true, forceClosed = false, onContentClick, children, }) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (forceClosed)
            setOpen(false);
    }, [forceClosed]);
    const handleClickCapture = useCallback(() => {
        setOpen(false);
    }, [setOpen]);
    const handleFocusCapture = useCallback((e) => {
        // Prevent focus from propagating to the Radix HoverCard trigger.
        // Without this, closing an overlay returns focus to the trigger,
        // which Radix interprets as a reason to reopen the hover card.
        e.stopPropagation();
    }, []);
    const handleContentClick = useCallback(() => {
        setOpen(false);
        onContentClick?.();
    }, [onContentClick]);
    if (!showHoverCard)
        return _jsx(_Fragment, { children: children });
    return (_jsx(Popover, { interaction: "hover", open: forceClosed ? false : open, onOpenChange: (isOpen) => {
            if (!forceClosed)
                setOpen(isOpen);
        }, openDelayMs: 300, closeDelayMs: 200, maxWidthPx: widthPx, 
        // The trigger can be a full-width row; keep the card at its own fixed
        // width instead of stretching to match the trigger.
        matchTriggerWidth: false, side: side, align: "start", offsetPx: offsetPx, triggerElement: _jsx("div", { className: triggerClassName, onClickCapture: handleClickCapture, onFocusCapture: handleFocusCapture, children: children }), children: _jsxs("div", { role: "button", tabIndex: 0, className: "-m-2 overflow-hidden rounded-lg cursor-pointer text-left", style: { width: widthPx }, 
            // Stop propagation so clicks inside the popover content don't bubble up
            // to the trigger element and fire its click handler a second time.
            onClick: (e) => {
                // Application-specific: keep preview clicks from also activating the row trigger.
                e.stopPropagation();
                handleContentClick();
            }, onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    // Prevent Enter/Space default (scroll/submit) and bubbling to the trigger.
                    e.preventDefault();
                    e.stopPropagation();
                    handleContentClick();
                }
            }, children: [_jsx("div", { className: "relative aspect-video w-full overflow-hidden", children: preview }), _jsxs("div", { className: "flex items-center gap-1 border-t p-2", style: { borderColor: 'rgb(from var(--fg-primary) r g b / 0.1)' }, children: [faviconDomain ? (_jsx(LinkFavicon, { domain: faviconDomain, size: 16, hideBorder: true })) : (_jsx(IconPrimitive, { icon: icon, size: LegacyTablerIconSize.sm, className: "shrink-0" })), _jsx(TextPrimitive, { variant: "small", textAlign: "start", className: "min-w-0", truncate: true, children: name }), typeLabel && (_jsx(TextPrimitive, { variant: "small", color: "light", className: "ml-auto shrink-0", children: typeLabel }))] })] }) }));
}
/**
 * A clickable file button (the trigger renders as a button) wrapped in the file
 * hover-preview popover. Shared by AssetFileButton and AttachmentFileButton.
 */
export function FileButton({ name, icon, faviconDomain, preview, showHoverCard = true, onClick, children, }) {
    // WHY stopPropagation/preventDefault: when rendered inside a LinkComponent (<a> tag) in the Table,
    // clicking the file button would also trigger row navigation without this.
    const handleClick = onClick
        ? (e) => {
            // Application-specific: keep the containing row from handling the same click.
            e.stopPropagation();
            // Prevent the enclosing link's browser navigation; this button owns the action.
            e.preventDefault();
            onClick();
        }
        : undefined;
    return (_jsx(FileHoverPreview, { name: name, icon: icon, faviconDomain: faviconDomain, preview: preview, showHoverCard: showHoverCard, onContentClick: onClick, children: _jsx(ButtonPrimitive, { type: "button", className: "min-w-0 w-full cursor-pointer text-left relative group py-1 hover:!text-primary rounded-sm", onClick: handleClick, children: _jsx("span", { className: "group-hover:!text-primary", children: children }) }) }));
}
//# sourceMappingURL=FileHoverCard.js.map